import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import marketingPackService from '../services/marketingPackService';
import './MarketingPack.css';

const ALLOWED_PAGES = new Set([
    'index.html',
    'partner-sales-guide.html',
    'hotel-one-pager.html',
    'objection-handling.html',
    'faq.html',
    'sync-one-pager.html',
    'payment-porter-one-pager.html',
]);

function MarketingPack() {
    const { isPartner, isSuperAdmin } = useAuth();
    const [currentPage, setCurrentPage] = useState('index.html');
    const [blobUrl, setBlobUrl]         = useState(null);
    const [error, setError]             = useState(null);
    const [styleCss, setStyleCss]       = useState('');
    const iframeRef = useRef(null);

    // Fetch shared stylesheet once.
    useEffect(() => {
        if (!isPartner && !isSuperAdmin) return;
        marketingPackService.getFile('style.css')
            .then(res => setStyleCss(res.content || ''))
            .catch(() => setStyleCss(''));
    }, [isPartner, isSuperAdmin]);

    // Fetch the current page (and inline the stylesheet) → create blob URL → render in iframe.
    useEffect(() => {
        if (!isPartner && !isSuperAdmin) return;
        if (!ALLOWED_PAGES.has(currentPage)) { setError('Page not found'); return; }

        let revoked = null;
        marketingPackService.getFile(currentPage)
            .then(res => {
                const html = (res.content || '').replace(
                    '<link rel="stylesheet" href="style.css">',
                    styleCss ? `<style>${styleCss}</style>` : ''
                );
                const blob = new Blob([html], { type: 'text/html' });
                const url  = URL.createObjectURL(blob);
                revoked = url;
                setBlobUrl(url);
                setError(null);
            })
            .catch(err => setError(err.response?.data?.error?.message || 'Failed to load marketing pack'));

        return () => { if (revoked) URL.revokeObjectURL(revoked); };
    }, [currentPage, styleCss, isPartner, isSuperAdmin]);

    // Intercept anchor clicks inside the iframe so internal links route through state instead of
    // hitting the iframe's blob origin (which has no other pages).
    const handleIframeLoad = () => {
        const iframe = iframeRef.current;
        if (!iframe || !iframe.contentDocument) return;
        const links = iframe.contentDocument.querySelectorAll('a[href]');
        links.forEach(a => {
            const href = a.getAttribute('href') || '';
            if (ALLOWED_PAGES.has(href)) {
                a.addEventListener('click', (e) => {
                    e.preventDefault();
                    setCurrentPage(href);
                });
            }
        });
    };

    if (!isPartner && !isSuperAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <div className="marketing-pack-container">
            {error ? (
                <div style={{ padding: '2rem', color: '#dc2626' }}>{error}</div>
            ) : blobUrl ? (
                <iframe
                    ref={iframeRef}
                    src={blobUrl}
                    title="Hub by HConnect — Partner Marketing Pack"
                    className="marketing-pack-frame"
                    onLoad={handleIframeLoad}
                />
            ) : (
                <div style={{ padding: '2rem', color: '#6b7280' }}>Loading…</div>
            )}
        </div>
    );
}

export default MarketingPack;
